'use memo';

import { createFileRoute } from '@tanstack/react-router';
import { Suspense, useCallback, useMemo, useState } from 'react';
import { Card, CardContent } from '@/components/Card/card';
import Input from '@/components/Input/Input';
import { HStack, Spacer, VStack } from '@/components/Layout';
import { ViewportLoader } from '@/components/Loading';
import StepperNavigation from '@/components/Navigation/StepperNavigation';
import { ControlledSelect } from '@/components/Select/ControlledSelect';
import DockAddStepperAction from '@/components/Stepper/DockAddStepperAction';
import { Text } from '@/components/Typography/Text';
import useDocumentTitle from '@/hooks/useDocumentTitle';
import { defaultNS, useTranslation } from '@/lib/i18n';
import { FormField } from '@/routes/_staff/person/-components/form/FormField';
import { useInboundDataPipelineEnums } from '../../../-query';
import { useCreateOutboundDataPipeline } from './-query';
export const Route = createFileRoute('/_staff/settings/data-connector/outbound/data-pipeline/add/')({
  component: OutboundAddPipelinePage
});

// 現状 OUT は PEOPLE のみ提供（backend `OutboundCreatableDataTypeSchema` と一致）。
// DEVICE 等は将来提供時に options を追加する（contract / entity 側に布石は残っている）。
type DataType = 'PEOPLE';
const ADD_PIPELINE_LINK = '/settings/data-connector/outbound/data-pipeline/add';

/**
 * OUT パイプライン追加。IN add ページと同型のレイアウト：
 * 左に StepperNavigation（基本情報）＋右にフォームカード。文言は IN と共通の
 * i18n（inboundDataPipeline.add.*）を再利用する。OUT の入力は name / dataType のみ。
 */
function OutboundAddPipelinePage() {
  const {
    t
  } = useTranslation(defaultNS, {
    keyPrefix: 'pages.dataConnector.inboundDataPipeline.add'
  });
  useDocumentTitle(t('pageTitle'));
  return <HStack className="w-full p-6 h-[calc(100vh-64px)]">
      <InnerNavigation />
      <Spacer variant="m" />
      <Suspense fallback={<ViewportLoader />}>
        <OutboundAddPipelineForm />
      </Suspense>
    </HStack>;
}
function InnerNavigation() {
  const {
    t
  } = useTranslation(defaultNS, {
    keyPrefix: 'pages.dataConnector.inboundDataPipeline.add'
  });
  const sections = [{
    key: 'basicInfo',
    hash: 'basic-info',
    label: t('stepper.basicInfo'),
    completed: false,
    error: false
  }];
  return <StepperNavigation className="sticky h-full" link={{
    hash: 'basic-info',
    to: ADD_PIPELINE_LINK
  }} title={t('title')} sections={sections} />;
}
function OutboundAddPipelineForm() {
  const {
    t
  } = useTranslation(defaultNS, {
    keyPrefix: 'pages.dataConnector.inboundDataPipeline.add'
  });
  const {
    submit,
    isPending
  } = useCreateOutboundDataPipeline();
  const [name, setName] = useState('');
  const [dataType, setDataType] = useState<DataType>('PEOPLE');

  // dataType の選択肢・ラベルは IN と同じ enums（サーバ多言語ラベル）を使い、
  // 作成可能な PEOPLE のみに絞る（括弧付き enum 表記にしない）。
  const {
    data: enums
  } = useInboundDataPipelineEnums();
  const dataTypeOptions = useMemo(() => (enums?.dataTypes ?? []).filter(item => item.value === 'PEOPLE').map(item => ({
    value: item.value,
    label: item.label
  })), [enums]);
  const fetchDataTypeOptions = useCallback(() => Promise.resolve(dataTypeOptions), [dataTypeOptions]);
  const onSubmit = useCallback(() => {
    submit({
      name: name.trim(),
      dataType,
      operationMode: 'RUNNING',
      triggerMode: 'MANUAL',
      ingestionMode: 'FULL'
    });
  }, [name, dataType, submit]);
  return <VStack className="gap-6 w-full min-w-[340px]">
      <Card className="p-4 rounded-lg bg-neutral-fill outline-solid outline-1 -outline-offset-1 outline-neutral-muted-border shadow-md">
        <Text variant="h3">{`1. ${t('stepper.basicInfo')}`}</Text>
      </Card>

      <Card className="rounded-lg bg-neutral-fill outline-solid outline-1 -outline-offset-1 outline-neutral-muted-border shadow-md">
        <CardContent className="pt-6">
          <VStack className="gap-4">
            <FormField fieldTitle={t('form.name.label')} fieldInput={<Input id="name" value={name} width={320} placeholder={t('form.name.placeholder')} onChange={e => setName(e.target.value)} />} />
            <FormField fieldTitle={t('form.dataType.label')} fieldInput={<ControlledSelect id="dataType" value={dataType} onChange={option => {
            if (option?.value === 'PEOPLE') {
              setDataType('PEOPLE');
            }
          }} fetchOptions={fetchDataTypeOptions} hideSearch width={320} />} />
          </VStack>
        </CardContent>
      </Card>

      <DockAddStepperAction baseLink={ADD_PIPELINE_LINK} previousPage={null} nextPage={null} nextComplete disabled={isPending || name.trim() === ''} onNextClick={onSubmit} onCompleteClick={onSubmit} />
    </VStack>;
}